import { Button, Card, SvgIcon } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { Operator } from '../../../types/operator';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';
import OperatorBadge from './OperatorBadge';

const OperatorsPage = () => {
  const { t } = useLanguage();
  const { getOperators } = useIotsApi();

  const {
    data: operators = [],
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: () => getOperators(),
  });

  useQueryErrorNotifier([error]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const columns = useMemo<GridColDef<Operator>[]>(
    () => [
      {
        field: 'name',
        flex: 3,
        headerName: t('content.company-name'),
        minWidth: 200,
      },
      {
        field: 'email',
        flex: 3,
        headerName: t('content.company-email-address'),
        minWidth: 200,
      },
      {
        field: 'phone',
        flex: 1,
        headerName: t('content.company-phone-number'),
        minWidth: 160,
      },
      {
        field: 'isActive',
        display: 'flex',
        minWidth: 140,
        headerName: t('content.status'),
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: t('content.active') },
          { value: false, label: t('content.inactive') },
        ],
        renderCell: params => (
          <OperatorBadge
            isActive={params.value as Operator['isActive']}
            small
          />
        ),
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: ({ row: { id } }) => {
          return (
            <AdminTableDetailLink
              isLoading={isFetching}
              href={`/admin/operators/${id}`}
            />
          );
        },
      },
    ],
    [t, isFetching],
  );

  return (
    <MainSectionTemplate
      title={t('content.operators')}
      actions={
        <Link to="/admin/operators/create">
          <Button
            startIcon={<SvgIcon component={RiAddLine} size="small" />}
            size="small"
            variant="contained"
          >
            {t('content.add-new')}
          </Button>
        </Link>
      }
    >
      <Card variant="outlined">
        <CustomDataGrid
          rows={operators}
          loading={isLoading}
          columns={columns}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            filter: {
              filterModel: initialFilterModel,
            },
            sorting: {
              sortModel: [
                {
                  field: 'name',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Card>
    </MainSectionTemplate>
  );
};

export default OperatorsPage;
